/**
 * O que o bloco mostra ao abrir no editor (E202.F2, GUS-145, ADR-0145 §6–§7), puro:
 *
 * - com rascunho guardado: o rascunho (a ação, o texto em markup e o fundamento);
 * - o parágrafo, o item e o texto da caixa: o que o papel mostra hoje (o negrito do original fica);
 * - a nota e o campo do risco: o que sai hoje no documento (`no_laudo`), sem o rótulo da nota.
 *
 * `noDocumento` é o texto que sai hoje, em markup: salvar só faz sentido quando o texto muda.
 */
import type { JSONContent } from '@tiptap/core';

import type { AcaoDaEdicao, TrechoNaTelaData } from '@/lib/correcaoDoRascunho';
import type { ParagrafoDoBlocoData, TipoDoBloco } from '@/lib/documentoDaSaida';
import type { ConteudoDoEditor, RascunhoNaTelaData } from '@/lib/editorManual';
import { docDoBloco, docDoMarkup, markupDoDoc, type ModoDaEdicao } from '@/lib/markupDaEdicao';

export interface InicioDoBloco {
  doc: JSONContent;
  conteudo: ConteudoDoEditor;
  noDocumento: string;
}

/** O que o trecho aceita no editor, pelo tipo dele e do bloco em que está. */
export function modoDoTrecho(trecho: TrechoNaTelaData, bloco: TipoDoBloco | null): ModoDaEdicao {
  if (trecho.tipo === 'campo') return 'simples';
  return bloco === 'itens' ? 'item' : 'rico';
}

function docDoQueSai(
  trecho: TrechoNaTelaData,
  paragrafos: readonly ParagrafoDoBlocoData[],
  modo: ModoDaEdicao,
): JSONContent {
  if (trecho.tipo === 'texto' && paragrafos.length > 0) return docDoBloco(paragrafos, modo);
  return docDoMarkup(trecho.no_laudo ?? trecho.base ?? '', modo);
}

export function inicioDoBloco(
  trecho: TrechoNaTelaData,
  paragrafos: readonly ParagrafoDoBlocoData[],
  modo: ModoDaEdicao,
  rascunho: RascunhoNaTelaData | undefined,
  pedida: AcaoDaEdicao | null,
): InicioDoBloco {
  const doDocumento = docDoQueSai(trecho, paragrafos, modo);
  const noDocumento = markupDoDoc(doDocumento, modo);
  if (rascunho !== undefined) {
    const doc = rascunho.texto !== '' ? docDoMarkup(rascunho.texto, modo) : doDocumento;
    return {
      doc,
      conteudo: { acao: rascunho.acao, texto: rascunho.texto, fundamento: rascunho.fundamento },
      noDocumento,
    };
  }
  const acao = pedida ?? (trecho.tipo === 'nota' ? 'escrever' : 'substituir');
  return { doc: doDocumento, conteudo: { acao, texto: noDocumento, fundamento: '' }, noDocumento };
}
